import { FC, useContext, useEffect, useRef, useState } from "react";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../context/notificationContext";
import useDataInitializer from "../../hooks/useDataInitializer";
import { useI18n } from "../../hooks/useTranslations";
import { useCarSubsidiariesApi } from "../../http/carSubsidiaries/api";
import { CarSubsidiaryNoCarDepency } from "../../http/carSubsidiaries/types";
import { NestedPick } from "../../types";
import ModalDialog from "../ui/ModalDialog";
import CarSubsidiaryForm, { CarSubsidiaryFormBody } from "./CarSubsidiaryForm";

const CAR_SUBSIDIARY_FIELDS = [
  "id",
  "car.id",
  "startDate",
  "endDate",
  "subsidiary.id",
  "subsidiary.name",
  "versionNumber",
] as const;
type CarSubsidiaryField = (typeof CAR_SUBSIDIARY_FIELDS)[number];
type CarSubsidiarySearch = NestedPick<CarSubsidiaryNoCarDepency, CarSubsidiaryField>;

interface CreateCarSubsidiaryButtonProps {
  onUpdate: () => void;
  onClose: () => void;
  carId: string;
}

const CreateCarSubsidiaryButton: FC<CreateCarSubsidiaryButtonProps> = ({ onUpdate, carId, onClose }) => {
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);
  const aborter = useRef(new AbortController());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const carSubsidiariesApi = useCarSubsidiariesApi();

  const result = carSubsidiariesApi.useCarSubsidiaries(
    { orderBy: "carSubsidiary.endDate", desc: true, fields: CAR_SUBSIDIARY_FIELDS },
    aborter.current.signal,
    carId,
  );
  const [data, isInitialized] = useDataInitializer(result.data, result.error);

  useEffect(() => {
    result.error && dispatch(showErrorNotification(i18n, result.error));
  }, [result.error]);

  const currentCarSubsidiary = data?.items.reduce((rv: CarSubsidiarySearch | undefined, i) => {
    return !rv || rv.startDate < i.startDate ? i : rv;
  }, undefined);

  const onSubmit = async (formData: CarSubsidiaryFormBody) => {
    setIsSubmitting(true);
    try {
      if (currentCarSubsidiary && formData.prevEndDate) {
        await carSubsidiariesApi.upsertCarSubsidiary(
          {
            carSubsidiary: {
              subsidiaryId: formData.subsidiary.id,
              startDate: formData.startDate,
              endDate: formData.endDate,
              prevEndDate: formData.prevEndDate,
              currentVersionNumber: currentCarSubsidiary.versionNumber,
            },
          },
          currentCarSubsidiary.car.id,
        );
      } else {
        await carSubsidiariesApi.insertCarSubsidiary(
          {
            carId: carId,
            subsidiaryId: formData.subsidiary.id,
            startDate: formData.startDate,
            endDate: formData.endDate,
          },
          carId,
        );
      }
      dispatch(showSuccessNotification(i18n.t));
      onUpdate();
    } catch (error) {
      dispatch(showErrorNotification(i18n, error));
    } finally {
      onClose();
      setIsSubmitting(false);
    }
  };

  return (
    <ModalDialog onClose={onClose} title={i18n.t.carSubsidiaries.new}>
      <CarSubsidiaryForm
        isDisabled={isSubmitting}
        onSubmit={onSubmit}
        currentCarSubsidiary={currentCarSubsidiary}
        isLoading={!isInitialized}
        onCloseEntitiesDialog={() => {
          onUpdate();
          onClose();
        }}
      />
    </ModalDialog>
  );
};

export default CreateCarSubsidiaryButton;
